/**
 * Coin dialog over the duel board. Faces come from DuelCoinArt so a
 * cosmetic can swap the texture later. The house face is the shop token.
 */

import * as THREE from 'three'
import { t } from '../../i18n/t'
import {
  DUELING_CHAOS_MARK_URL,
  HOUSE_DUEL_COIN,
  duelCoinArt,
  type DuelCoinArt,
} from './duelCoin'
import './CoinToss.css'

export type CoinFace = 'heads' | 'tails'

const SPIN_MS = 1400
const STAGGER_MS = 380
const RESULT_HOLD_MS = 3000

/** How the dialog itself closed. The scene adds deadline, off, and stuck. */
export type CoinDialogClose = 'button' | 'done' | 'ceiling'

/**
 * Wall-clock pause for one toss: the spin, the stagger between coins, and
 * the 3 second result. Armed before the first frame so a stuck paint cannot
 * hold the duel open.
 */
export function coinPauseBudgetMs(
  count: number,
  reduced: boolean,
  scale: (ms: number) => number,
): number {
  const n = Math.max(1, count)
  const spin = reduced ? 0 : Math.max(0, scale(SPIN_MS))
  const stagger = reduced ? 0 : Math.max(0, scale(STAGGER_MS))
  return spin + stagger * Math.max(0, n - 1) + RESULT_HOLD_MS
}
/** The tap that called Cara/Cruz must not also hit Cerrar. */
const CLOSE_ARM_MS = 500

const easeOutCubic = (u: number): number => 1 - (1 - u) ** 3

let active: { finish: (how?: CoinDialogClose) => void } | null = null
let warmed: Promise<HTMLImageElement> | null = null

/**
 * One WebGL context for every toss. A new renderer each activation never
 * released its context (`dispose` does not), and a phone drops the oldest
 * one — the duel board — on the third Barrel Dragon activation.
 */
let sharedGl: { renderer: THREE.WebGLRenderer; canvas: HTMLCanvasElement } | null = null

function coinRenderer(): { renderer: THREE.WebGLRenderer; canvas: HTMLCanvasElement } {
  const live = sharedGl
  const gl = live?.renderer.getContext()
  if (live && gl && !gl.isContextLost()) return live
  if (live) {
    live.renderer.forceContextLoss()
    live.renderer.dispose()
    live.canvas.remove()
    sharedGl = null
  }
  const canvas = document.createElement('canvas')
  canvas.className = 'coin-toss-canvas'
  const phone =
    typeof window !== 'undefined' && window.matchMedia('(pointer: coarse)').matches
  const renderer = new THREE.WebGLRenderer({
    canvas,
    antialias: !phone,
    alpha: true,
    powerPreference: 'high-performance',
  })
  renderer.outputColorSpace = THREE.SRGBColorSpace
  renderer.toneMapping = THREE.ACESFilmicToneMapping
  renderer.toneMappingExposure = 1.15
  renderer.setClearColor(0x000000, 0)
  sharedGl = { renderer, canvas }
  return sharedGl
}

/** Keep the canvas in the document so removing the dialog does not drop the context. */
function parkCoinCanvas(canvas: HTMLCanvasElement): void {
  let park = document.getElementById('coin-gl-park')
  if (!park) {
    park = document.createElement('div')
    park.id = 'coin-gl-park'
    park.setAttribute('aria-hidden', 'true')
    park.style.cssText =
      'position:fixed;left:0;top:0;width:8px;height:8px;overflow:hidden;opacity:0;pointer-events:none;'
    document.body.append(park)
  }
  park.append(canvas)
}

/** Start fetching the house coin so the first toss does not wait on the network. */
export function warmDuelCoin(): void {
  if (!warmed) warmed = loadImage(HOUSE_DUEL_COIN.headsUrl)
}

/** Close a toss that never called its done callback. Safe when none is open. */
export function cancelDuelCoinToss(): void {
  active?.finish()
}

export async function playDuelCoinToss(
  host: HTMLElement,
  results: readonly CoinFace[],
  opts: {
    reduced: boolean
    art?: DuelCoinArt
    onDone?: (how: CoinDialogClose) => void
    /** Same scale as the duel animation-speed control. */
    scale?: (ms: number) => number
    /** Skip the 3D coin and show the flat one. Used when WebGL did not paint. */
    flat?: boolean
  },
): Promise<void> {
  if (results.length === 0) {
    opts.onDone?.('done')
    return
  }
  active?.finish('done')
  const onDone = opts.onDone ?? (() => {})
  if (opts.flat) {
    showFlatCoin(host, results, onDone)
    return
  }
  const art = opts.art ?? duelCoinArt()
  const scale = opts.scale ?? ((ms: number) => ms)
  const show = new CoinTossShow(host, results, opts.reduced, art, onDone, scale)
  active = show
  try {
    await show.start()
  } catch {
    if (show.closed) return
    show.drop()
    if (active === show) active = null
    showFlatCoin(host, results, onDone)
  }
}

interface CoinDialog {
  root: HTMLDivElement
  stage: HTMLDivElement
  result: HTMLParagraphElement
  close: () => void
  release: () => void
}

/**
 * Sit on document.body, over the board rectangle. A child of .board-stage
 * is painted under the prompt (z-index 220) and Vue deletes it on the next
 * render of that div.
 */
function placeOverBoard(root: HTMLElement, anchor: HTMLElement): void {
  const box = anchor.isConnected ? anchor.getBoundingClientRect() : null
  if (!box || box.width < 8 || box.height < 8) {
    root.style.top = '0'
    root.style.left = '0'
    root.style.width = '100%'
    root.style.height = '100%'
    return
  }
  root.style.top = `${box.top}px`
  root.style.left = `${box.left}px`
  root.style.width = `${box.width}px`
  root.style.height = `${box.height}px`
}

function mountDialog(anchor: HTMLElement, count: number, close: () => void): CoinDialog {
  const root = document.createElement('div')
  root.className = 'coin-toss-root'
  const panel = document.createElement('div')
  panel.className = `coin-toss-panel${count > 1 ? ' is-multi' : ''}`
  panel.setAttribute('role', 'dialog')
  panel.setAttribute('aria-label', t('duel.coin.close'))

  const acceptCloseAt = performance.now() + CLOSE_ARM_MS
  const requestClose = (): void => {
    if (performance.now() < acceptCloseAt) return
    close()
  }

  const x = document.createElement('button')
  x.type = 'button'
  x.className = 'coin-toss-x'
  x.setAttribute('aria-label', t('duel.coin.close'))
  x.textContent = '×'
  x.addEventListener('click', requestClose)

  const stage = document.createElement('div')
  stage.className = 'coin-toss-stage'

  const result = document.createElement('p')
  result.className = 'coin-toss-result'

  const button = document.createElement('button')
  button.type = 'button'
  button.className = 'coin-toss-close'
  button.textContent = t('duel.coin.close')
  button.addEventListener('click', requestClose)

  panel.append(x, stage, result, button)
  root.append(panel)
  document.body.append(root)
  placeOverBoard(root, anchor)
  const onReflow = (): void => placeOverBoard(root, anchor)
  window.addEventListener('resize', onReflow)
  window.addEventListener('scroll', onReflow, true)
  const release = (): void => {
    window.removeEventListener('resize', onReflow)
    window.removeEventListener('scroll', onReflow, true)
    root.remove()
  }
  return { root, stage, result, close, release }
}

/** If WebGL fails, still show the official coin so the toss is not invisible. */
function showFlatCoin(
  host: HTMLElement,
  results: readonly CoinFace[],
  onDone: (how: CoinDialogClose) => void,
): void {
  let closed = false
  let timer = 0
  let labelTimer = 0
  const dialog = mountDialog(host, results.length, () => finish('button'))
  const finish = (how: CoinDialogClose = 'done'): void => {
    if (closed) return
    closed = true
    window.clearTimeout(timer)
    window.clearTimeout(labelTimer)
    dialog.release()
    if (active === handle) active = null
    onDone(how)
  }
  const handle = { finish }
  active = handle
  const row = document.createElement('div')
  row.className = 'coin-toss-flat'
  for (const side of results) {
    const img = document.createElement('img')
    img.src = HOUSE_DUEL_COIN.headsUrl
    img.alt = ''
    if (side === 'tails') img.style.filter = 'brightness(0.62) saturate(0.8)'
    row.append(img)
  }
  dialog.stage.append(row)
  const words = results.map(faceWord).join(', ')
  labelTimer = window.setTimeout(() => {
    if (!closed) dialog.result.textContent = words
  }, 1200)
  timer = window.setTimeout(() => finish('done'), RESULT_HOLD_MS)
}

function faceWord(side: CoinFace): string {
  return t(side === 'heads' ? 'duel.coin.heads' : 'duel.coin.tails')
}

function loadImage(url: string): Promise<HTMLImageElement> {
  if (url === HOUSE_DUEL_COIN.headsUrl && warmed) return warmed
  const pending = new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error(url))
    img.src = url
  })
  if (url !== HOUSE_DUEL_COIN.headsUrl) return pending
  warmed = pending.catch((err: unknown) => {
    warmed = null
    throw err
  })
  return warmed
}

/**
 * Reverse face: the gold coin's metal, with the Dueling Chaos mark engraved
 * in that same texture. A cosmetic `tailsUrl` replaces this.
 */
function brandCoinFace(coin: CanvasImageSource, mark: CanvasImageSource): HTMLCanvasElement {
  const w = imageWidth(coin)
  const h = imageHeight(coin)
  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h
  const ctx = canvas.getContext('2d')
  if (!ctx) return canvas
  ctx.drawImage(coin, 0, 0, w, h)
  const cx = w / 2
  const cy = h / 2
  const inner = Math.min(w, h) * 0.36
  ctx.save()
  ctx.beginPath()
  ctx.arc(cx, cy, inner, 0, Math.PI * 2)
  ctx.clip()
  ctx.fillStyle = 'rgba(186, 138, 42, 0.88)'
  ctx.fillRect(0, 0, w, h)
  ctx.restore()

  const stamp = document.createElement('canvas')
  stamp.width = w
  stamp.height = h
  const sctx = stamp.getContext('2d')
  if (!sctx) return canvas
  const size = inner * 1.55
  sctx.drawImage(mark, cx - size / 2, cy - size / 2, size, size)
  const pixels = sctx.getImageData(0, 0, w, h)
  const data = pixels.data
  for (let i = 0; i < data.length; i += 4) {
    const gold = data[i] > 140 && data[i + 1] > 100 && data[i] > data[i + 2] + 30
    if (!gold) data[i + 3] = 0
  }
  sctx.putImageData(pixels, 0, 0)

  const relief = document.createElement('canvas')
  relief.width = w
  relief.height = h
  const rctx = relief.getContext('2d')
  if (!rctx) return canvas
  rctx.drawImage(canvas, 0, 0)
  rctx.globalCompositeOperation = 'destination-in'
  rctx.drawImage(stamp, 0, 0)
  rctx.globalCompositeOperation = 'source-atop'
  rctx.fillStyle = 'rgba(48, 28, 6, 0.42)'
  rctx.fillRect(0, 0, w, h)

  ctx.save()
  ctx.shadowColor = 'rgba(0, 0, 0, 0.55)'
  ctx.shadowBlur = Math.max(2, w * 0.01)
  ctx.shadowOffsetY = Math.max(1, h * 0.006)
  ctx.drawImage(relief, 0, 0)
  ctx.restore()

  ctx.beginPath()
  ctx.arc(cx, cy, inner, 0, Math.PI * 2)
  ctx.strokeStyle = '#f0d78a'
  ctx.lineWidth = Math.max(2, Math.min(w, h) * 0.014)
  ctx.stroke()
  return canvas
}

function imageWidth(image: CanvasImageSource): number {
  if (image instanceof HTMLImageElement && image.naturalWidth > 0) return image.naturalWidth
  if (image instanceof HTMLCanvasElement && image.width > 0) return image.width
  return 512
}

function imageHeight(image: CanvasImageSource): number {
  if (image instanceof HTMLImageElement && image.naturalHeight > 0) return image.naturalHeight
  if (image instanceof HTMLCanvasElement && image.height > 0) return image.height
  return 512
}

async function loadMark(): Promise<HTMLImageElement> {
  const res = await fetch(DUELING_CHAOS_MARK_URL)
  if (!res.ok) throw new Error(DUELING_CHAOS_MARK_URL)
  const text = await res.text()
  const sized = text.includes('width=')
    ? text
    : text.replace('<svg ', '<svg width="512" height="512" ')
  const url = URL.createObjectURL(new Blob([sized], { type: 'image/svg+xml' }))
  try {
    return await loadImage(url)
  } finally {
    URL.revokeObjectURL(url)
  }
}

/** Dark mirror of the coin if the mark cannot be drawn. */
function reverseFace(image: CanvasImageSource, w: number, h: number): HTMLCanvasElement {
  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h
  const ctx = canvas.getContext('2d')
  if (!ctx) return canvas
  ctx.translate(w, 0)
  ctx.scale(-1, 1)
  ctx.drawImage(image, 0, 0, w, h)
  ctx.setTransform(1, 0, 0, 1, 0, 0)
  ctx.globalCompositeOperation = 'source-atop'
  ctx.fillStyle = 'rgba(62, 36, 8, 0.42)'
  ctx.fillRect(0, 0, w, h)
  return canvas
}

class CoinTossShow {
  private readonly host: HTMLElement
  private readonly results: readonly CoinFace[]
  private readonly art: DuelCoinArt
  private readonly onDone: (how: CoinDialogClose) => void
  private readonly spinMs: number
  private readonly staggerMs: number
  private root: HTMLDivElement | null = null
  private releaseDialog: (() => void) | null = null
  private resultEl: HTMLParagraphElement | null = null
  private renderer: THREE.WebGLRenderer | null = null
  private scene: THREE.Scene | null = null
  private camera: THREE.PerspectiveCamera | null = null
  private resizeObs: ResizeObserver | null = null
  private raf = 0
  private closeTimer = 0
  private bootTimer = 0
  private ceilingTimer = 0
  private disposed = false
  private finished = false
  private holdStarted = false
  private startedAt = 0
  private readonly disposables: { dispose: () => void }[] = []
  private readonly coins: {
    mesh: THREE.Mesh
    shadow: THREE.Mesh
    shadowMat: THREE.MeshBasicMaterial
    side: CoinFace
    landAt: number
  }[] = []

  constructor(
    host: HTMLElement,
    results: readonly CoinFace[],
    reduced: boolean,
    art: DuelCoinArt,
    onDone: (how: CoinDialogClose) => void,
    scale: (ms: number) => number,
  ) {
    this.host = host
    this.results = results
    this.art = art
    this.onDone = onDone
    this.spinMs = reduced ? 0 : Math.max(0, scale(SPIN_MS))
    this.staggerMs = reduced ? 0 : Math.max(0, scale(STAGGER_MS))
  }

  /** True after finish(), so a failed start does not also open the flat coin. */
  get closed(): boolean {
    return this.finished
  }

  async start(): Promise<void> {
    this.armBoot()
    const headsImg = await loadImage(this.art.headsUrl)
    if (this.finished || this.disposed) return
    const tailsSource = await this.tailsArt(headsImg)
    if (this.finished || this.disposed) return

    const dialog = mountDialog(this.host, this.results.length, () => this.finish('button'))
    if (this.finished || this.disposed) {
      dialog.release()
      return
    }
    this.root = dialog.root
    this.releaseDialog = dialog.release
    this.resultEl = dialog.result
    const { renderer, canvas } = coinRenderer()
    this.renderer = renderer
    dialog.stage.append(canvas)
    window.clearTimeout(this.bootTimer)
    this.bootTimer = 0
    this.armCeiling()

    const scene = new THREE.Scene()
    this.scene = scene
    const count = this.results.length
    const camera = new THREE.PerspectiveCamera(28, 1, 0.1, 40)
    camera.position.set(0, 0.12, count === 1 ? 4.6 : 5.4)
    camera.lookAt(0, 0, 0)
    this.camera = camera

    scene.add(new THREE.AmbientLight(0xfff4dd, 0.72))
    const key = new THREE.DirectionalLight(0xfff1c9, 1.55)
    key.position.set(2.2, 4.4, 5)
    scene.add(key)
    const rim = new THREE.DirectionalLight(0xffd27a, 0.55)
    rim.position.set(-3.2, 1.2, -2)
    scene.add(rim)

    const headsTex = this.textureFrom(headsImg)
    const tailsTex = this.textureFrom(tailsSource)
    const radius = count === 1 ? 0.72 : 0.5
    const gap = count === 1 ? 0 : 1.2
    const spinMs = this.spinMs
    const staggerMs = this.staggerMs
    const edge = new THREE.MeshStandardMaterial({
      color: 0xe2b34a,
      metalness: 0.92,
      roughness: 0.28,
    })
    this.disposables.push(edge)
    const headsMat = new THREE.MeshBasicMaterial({
      map: headsTex,
      alphaTest: 0.45,
      toneMapped: false,
    })
    const tailsMat = new THREE.MeshBasicMaterial({
      map: tailsTex,
      alphaTest: 0.45,
      toneMapped: false,
    })
    this.disposables.push(headsMat, tailsMat)

    this.results.forEach((side, index) => {
      const geo = new THREE.CylinderGeometry(radius, radius, radius * 0.16, 48)
      this.disposables.push(geo)
      const mesh = new THREE.Mesh(geo, [edge, headsMat, tailsMat])
      mesh.position.x = (index - (count - 1) / 2) * gap
      const shadowGeo = new THREE.CircleGeometry(radius * 0.82, 32)
      this.disposables.push(shadowGeo)
      const shadowMat = new THREE.MeshBasicMaterial({
        color: 0x000000,
        transparent: true,
        opacity: 0.28,
        depthWrite: false,
      })
      this.disposables.push(shadowMat)
      const shadow = new THREE.Mesh(shadowGeo, shadowMat)
      shadow.rotation.x = -Math.PI / 2
      shadow.position.set(mesh.position.x, -0.04, 0)
      scene.add(shadow, mesh)
      const landAt = spinMs === 0 ? 0 : index * staggerMs + spinMs
      this.coins.push({ mesh, shadow, shadowMat, side, landAt })
    })

    this.resize()
    this.resizeObs = new ResizeObserver(() => this.resize())
    this.resizeObs.observe(dialog.stage)
    this.startedAt = performance.now()
    this.raf = requestAnimationFrame(this.frame)
  }

  /** Close the dialog and let the duel continue. Safe to call twice. */
  finish(how: CoinDialogClose = 'done'): void {
    if (this.finished) return
    this.finished = true
    this.clearTimers()
    const done = this.onDone
    this.drop()
    done(how)
  }

  /**
   * The 3D context never painted. Show the flat coin and let that dialog
   * close the duel pause. finish() would skip the toss entirely.
   */
  private failToFlat(): void {
    if (this.finished) return
    this.finished = true
    this.clearTimers()
    const host = this.host
    const results = this.results
    const done = this.onDone
    this.drop()
    try {
      showFlatCoin(host, results, done)
    } catch {
      done('done')
    }
  }

  /** Tear down without telling the duel. Used when WebGL fails over to the flat coin. */
  drop(): void {
    if (this.disposed) return
    this.disposed = true
    if (active === this) active = null
    cancelAnimationFrame(this.raf)
    this.clearTimers()
    this.resizeObs?.disconnect()
    const canvas = sharedGl?.canvas
    if (canvas) parkCoinCanvas(canvas)
    this.releaseDialog?.()
    this.releaseDialog = null
    this.root = null
    this.resultEl = null
    for (const item of this.disposables) item.dispose()
    this.disposables.length = 0
    // Keep the shared renderer. Disposing it would leak the context and the
    // next toss would open another one.
    this.renderer = null
    this.scene = null
    this.camera = null
  }

  /** Image load hung. Do not wait on the Phaser clock forever. */
  private armBoot(): void {
    window.clearTimeout(this.bootTimer)
    this.bootTimer = window.setTimeout(() => this.finish('ceiling'), 8000)
  }

  /** Last coin never reported a landing. Close anyway. */
  private armCeiling(): void {
    window.clearTimeout(this.ceilingTimer)
    const spin = Number.isFinite(this.spinMs) ? this.spinMs : SPIN_MS
    const stagger = Number.isFinite(this.staggerMs) ? this.staggerMs : STAGGER_MS
    const cap =
      spin + stagger * Math.max(0, this.results.length - 1) + RESULT_HOLD_MS + 800
    this.ceilingTimer = window.setTimeout(() => this.finish('ceiling'), cap)
  }

  private clearTimers(): void {
    window.clearTimeout(this.closeTimer)
    window.clearTimeout(this.bootTimer)
    window.clearTimeout(this.ceilingTimer)
    this.closeTimer = 0
    this.bootTimer = 0
    this.ceilingTimer = 0
  }

  private async tailsArt(headsImg: HTMLImageElement): Promise<CanvasImageSource> {
    if (this.art.tailsUrl) return loadImage(this.art.tailsUrl)
    try {
      const mark = await loadMark()
      return brandCoinFace(headsImg, mark)
    } catch {
      return reverseFace(headsImg, imageWidth(headsImg), imageHeight(headsImg))
    }
  }

  private textureFrom(source: CanvasImageSource | HTMLImageElement): THREE.Texture {
    const tex = new THREE.Texture(source)
    tex.colorSpace = THREE.SRGBColorSpace
    tex.anisotropy = 8
    // Cylinder caps put the top of the art on the right. Turn it so the
    // dragon's head points up, the same way the shop token is drawn.
    tex.center.set(0.5, 0.5)
    tex.rotation = Math.PI / 2
    tex.needsUpdate = true
    this.disposables.push(tex)
    return tex
  }

  private resize(): void {
    const renderer = this.renderer
    const camera = this.camera
    const canvas = renderer?.domElement
    if (!renderer || !camera || !canvas) return
    const stage = canvas.parentElement
    const w = stage?.clientWidth ?? 0
    const h = stage?.clientHeight ?? 0
    if (w < 2 || h < 2) return
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2))
    renderer.setSize(w, h, false)
    camera.aspect = w / h
    camera.updateProjectionMatrix()
  }

  private frame = (): void => {
    if (this.disposed || this.finished) return
    if (!this.root?.isConnected) {
      this.finish()
      return
    }
    try {
      const elapsed = performance.now() - this.startedAt
      for (const coin of this.coins) this.pose(coin, elapsed, this.spinMs)
      this.syncResult(elapsed)
      const renderer = this.renderer
      const gl = renderer?.getContext()
      if (!renderer || !this.scene || !this.camera || !gl || gl.isContextLost()) {
        this.failToFlat()
        return
      }
      renderer.render(this.scene, this.camera)
    } catch {
      this.failToFlat()
      return
    }
    this.raf = requestAnimationFrame(this.frame)
  }

  private pose(
    coin: CoinTossShow['coins'][number],
    elapsed: number,
    spinMs: number,
  ): void {
    const startAt = Math.max(0, coin.landAt - spinMs)
    const waiting = elapsed < startAt
    coin.mesh.visible = !waiting
    coin.shadow.visible = !waiting
    if (waiting) return
    const end = coin.side === 'heads' ? Math.PI / 2 : Math.PI / 2 + Math.PI
    const p = spinMs === 0 ? 1 : Math.max(0, Math.min(1, (elapsed - startAt) / spinMs))
    const eased = spinMs === 0 ? 1 : easeOutCubic(p)
    const start = end + 3 * Math.PI * 2 + Math.PI / 2
    coin.mesh.rotation.x = start + (end - start) * eased
    coin.mesh.rotation.z = Math.sin(p * Math.PI) * 0.22 * (1 - eased)
    const pop = Math.min(1, p / 0.16)
    coin.mesh.scale.setScalar(0.45 + 0.55 * (spinMs === 0 ? 1 : pop))
    const toss = spinMs === 0 ? 0 : Math.sin(Math.min(1, p / 0.8) * Math.PI)
    coin.mesh.position.y = toss * 0.42
    coin.shadow.scale.setScalar(1 - toss * 0.35)
    coin.shadowMat.opacity = 0.28 * (1 - toss * 0.7)
  }

  private syncResult(elapsed: number): void {
    const result = this.resultEl
    if (!result) return
    const landed = this.coins.filter((coin) => elapsed >= coin.landAt)
    result.textContent = landed.map((coin) => faceWord(coin.side)).join(', ')
    if (landed.length !== this.coins.length || this.holdStarted) return
    this.holdStarted = true
    this.closeTimer = window.setTimeout(() => this.finish('done'), RESULT_HOLD_MS)
  }
}
